import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NumberUtils } from '@pk/powerkioskutils';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { pairwise, startWith } from 'rxjs/operators';
import { GraphqlService } from 'src/app/graphql/graphql.service';
import { NotificationService } from 'src/app/notification/services/notification.service';
import { Product, RfqSession, RfqSessionBid } from 'src/app/shared/models';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';
import { ConvertBidUnitsPipe } from 'src/app/shared/pipes/convert-bid-units.pipe';

@Component({
	selector: 'pk-broker-auction-bid-edit',
	templateUrl: './auction-bid-edit.component.html',
	styleUrl: './auction-bid-edit.component.scss',
	standalone: false,
})
export class AuctionBidEditComponent extends AbstractPageForm {
	@Input() bid: RfqSessionBid;
	@Input() rfqSession: RfqSession;

	@Output() bidUpdated = new EventEmitter<void>();

	public product: Product;
	public rfqSessionBid: RfqSessionBid;
	public bidMeasureUnits: { text: string; factor: number }[];
	public defaultUnits: { text: string; factor: number };

	constructor(
		private graphqlService: GraphqlService,
		private fb: FormBuilder,
		private toastrService: ToastrService,
		private convertBidUnitsPipe: ConvertBidUnitsPipe,
		private notificationService: NotificationService,
	) {
		super();
		this.submitText = 'Update Bid';
		this.submitAudit = 'auction-bid-edit:submitBid';
	}

	public override async loadPageData() {
		try {
			this.bidMeasureUnits = _.orderBy(this.CONSTANTS.units[this.rfqSession.contract.serviceTypeId] ?? [], ['factor'], ['desc']);
			this.defaultUnits = this.bidMeasureUnits.find(u => u.factor === 1);
			console.log(this.bidMeasureUnits);
			console.log(this.defaultUnits);
			const result = await this.graphqlService.getRfqSessionEditBidData(this.bid.rfqSessionBidId, this.bid.rfqSessionProduct.productId);
			this.rfqSessionBid = new RfqSessionBid(result.data.rfqSessionBid);
			this.product = result.data.product;
			this.product.pricingComponents = _.sortBy(this.product.pricingComponents, 'name');
		} catch (e) {
			this.toastrService.error('There was a problem loading the bid data. We have been notified and are working to fix the issue. Please check back again in 30 minutes.', 'Pricing Session');
			this.userAuditService.captureEvent(this.CONSTANTS.userAuditTypes.code, e.message, 'auction-bid-edit:loadPageData');
		}
	}

	public getForm() {
		return this.fb.group({
			units: [
				this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units) ?? this.bidMeasureUnits[0],
				[Validators.required],
			],
			bidAmount: [
				NumberUtils.rateToUnits(
					this.rfqSessionBid.amount,
					this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1] ?? this.bidMeasureUnits[0].text.split('/')[1],
				),
				[Validators.required, Validators.min(0)],
			],
			bufferMargin: [
				NumberUtils.rateToUnits(
					this.rfqSessionBid.bufferMargin,
					this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1] ?? this.bidMeasureUnits[0].text.split('/')[1],
				),
				[Validators.required, Validators.min(0)],
			],
			packingMargin: [
				NumberUtils.rateToUnits(
					this.rfqSessionBid.packingMargin,
					this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1] ?? this.bidMeasureUnits[0].text.split('/')[1],
				),
				[Validators.required, Validators.min(0)],
			],
			pricingComponents: this.fb.array(
				this.product.pricingComponents.map(pc =>
					this.fb.group({
						pricingComponentId: pc.id,
						amount: NumberUtils.rateToUnits(
							this.rfqSessionBid.pricingComponents.find(b => pc.id === b.pricingComponentId)?.amount,
							this.bidMeasureUnits.find(u => u.text === this.rfqSessionBid?.pricingComponents[0]?.units)?.text?.split('/')[1] ?? this.bidMeasureUnits[0].text.split('/')[1],
						),
					}),
				),
			),
		});
	}

	public async onFormLoaded() {
		this.form.units.valueChanges.pipe(startWith(this.form.units.value), pairwise()).subscribe(([prevUnits, units]) => {
			const bidAmountDefaultUnits = NumberUtils.rateToTherms(this.form.bidAmount.value, prevUnits.text.split('/')[1]);
			const bufferMarginDefaultUnits = NumberUtils.rateToTherms(this.form.bufferMargin.value, prevUnits.text.split('/')[1]);
			const packingMarginDefaultUnits = NumberUtils.rateToTherms(this.form.packingMargin.value, prevUnits.text.split('/')[1]);

			this.form.bidAmount.setValue(NumberUtils.rateToUnits(bidAmountDefaultUnits, units.text.split('/')[1]));
			this.form.bufferMargin.setValue(NumberUtils.rateToUnits(bufferMarginDefaultUnits, units.text.split('/')[1]));
			this.form.packingMargin.setValue(NumberUtils.rateToUnits(packingMarginDefaultUnits, units.text.split('/')[1]));
			this.form.pricingComponents.forEach(pc => {
				const defaultAmount = NumberUtils.rateToTherms(pc.amount.value, prevUnits.text.split('/')[1]);
				pc.amount.setValue(NumberUtils.rateToUnits(defaultAmount, units.text.split('/')[1]));
			});
		});
	}

	public override async onFormSubmitted() {
		try {
			const dto = this.rfqSessionBidDto();
			if(this.finalBidRateChange !== 0) {
				const result = await this.notificationService.confirm(
					{
						title: 'Final bid rate has changed',
						body: `The final bid rate has changed from <strong>${this.originalFinalBidRate} ${this.form.units.value.text}</strong> to <strong>${this.totalBidRate} ${this.form.units.value.text}</strong>.`,
						body2: 'Are you sure you want to proceed?',
					},
					'yesNo',
				);
				if(!result) {
					return;
				}
			}
			await this.graphqlService.updateRfqSessionBid(this.bid.rfqSessionBidId, dto);
			this.toastrService.success('Bid updated successfully');
			this.bidUpdated.emit();
		} catch (e) {
			this.toastrService.warning(
				// eslint-disable-next-line max-len, max-len
				'There was a problem updating the bid. We have been notified and are working to fix the issue. Please check back again in 30 minutes.',
				'Pricing Session',
			);
			this.userAuditService.captureEvent(this.CONSTANTS.userAuditTypes.code, e.message, 'auction-bid-edit:submitBid');
		}
	}

	//Refactor this out to be variables
	get brokerMargin() {
		return this.convertBidUnitsPipe.transform(this.rfqSession.contract.getTotalMargin(false, true, false), this.form.units.value.text);
	}
	get totalBidRate() {
		return Math.round(((this.brokerMargin ?? 0) + (this.form.bufferMargin.value ?? 0) + (this.form.packingMargin.value ?? 0) + (this.form.bidAmount.value ?? 0)) * 1e12) / 1e12;
	}

	get originalBufferMargin() {
		return this.convertBidUnitsPipe.transform(this.rfqSessionBid.bufferMargin, this.form.units.value.text);
	}
	get bufferMarginChange() {
		return Math.round((Number(this.form.bufferMargin.value) - Number(this.originalBufferMargin)) * 1e12) / 1e12;
	}
	get originalPackingMargin() {
		return this.convertBidUnitsPipe.transform(this.rfqSessionBid.packingMargin, this.form.units.value.text);
	}
	get packingMarginChange() {
		return Math.round((Number(this.form.packingMargin.value) - Number(this.originalPackingMargin)) * 1e12) / 1e12;
	}
	get originalSupplierBaseBidRate() {
		return this.convertBidUnitsPipe.transform(this.rfqSessionBid.amount, this.form.units.value.text);
	}
	get supplierBaseBidRateChange() {
		return Math.round((Number(this.form.bidAmount.value) - Number(this.originalSupplierBaseBidRate)) * 1e12) / 1e12;
	}
	get originalFinalBidRate() {
		return Math.round(((this.brokerMargin ?? 0) + (this.originalBufferMargin ?? 0) + (this.originalPackingMargin ?? 0) + (this.originalSupplierBaseBidRate ?? 0)) * 1e12) / 1e12;
	}
	get finalBidRateChange() {
		return Math.round((Number(this.totalBidRate) - Number(this.originalFinalBidRate)) * 1e12) / 1e12;
	}

	private rfqSessionBidDto() {
		const dto: any = {
			pricingComponents: this.form.pricingComponents
				.map(pc => ({
					pricingComponentId: pc.pricingComponentId.value,
					amount: NumberUtils.rateToTherms(Number(pc.amount.value), this.form.units.value.text.split('/')[1]),
					units: this.form.units.value.text,
				}))
				.filter(pc => !_.isNil(pc.amount) && !_.isNaN(pc.amount) && pc.amount !== 0),
		};

		const bidAmount = NumberUtils.rateToTherms(Number(this.form.bidAmount.value), this.form.units.value.text.split('/')[1]);
		const bufferMargin = NumberUtils.rateToTherms(Number(this.form.bufferMargin.value), this.form.units.value.text.split('/')[1]);
		const packingMargin = NumberUtils.rateToTherms(Number(this.form.packingMargin.value), this.form.units.value.text.split('/')[1]);

		if (bidAmount !== this.rfqSessionBid.amount) {
			dto.amount = bidAmount;
			dto.rfqSessionBidStatusId = this.CONSTANTS.rfqSessionBidStatuses.edited;
		}
		if (bufferMargin !== this.rfqSessionBid.bufferMargin) {
			dto.bufferMargin = bufferMargin;
		}
		if (packingMargin !== this.rfqSessionBid.packingMargin) {
			dto.packingMargin = packingMargin;
		}

		return dto;
	}
}
